/**
 * The Automatic-disk-backup status line, derived from three independent facts.
 *
 * WHY THIS IS A PURE FUNCTION
 * ---------------------------
 * The badge was wrong because it was computed inline from two booleans that mean
 * different things, and one of them was `undefined` on first paint:
 *
 *   `status?.enabled !== false` reads as TRUE before the first status poll
 *   resolves, while "is the folder ready" starts FALSE — so the panel rendered
 *   "Paused — folder access was revoked" for one frame and then flipped to
 *   "Off — choose a folder", which is the contradictory On/Off the user saw.
 *
 * It also collapsed two different failures into one: a device with NO folder and
 * a device that HAS a folder whose permission needs re-granting both showed
 * "Off — choose a folder", so the only offered action was to pick a folder
 * again (which is exactly what must not happen to a saved folder).
 *
 * So the three facts are modelled explicitly, and "On" requires all of them:
 *
 *   readiness        — what the target reports without prompting the user
 *   configured       — a manifest was READ from the folder (backup set up)
 *   schedulerArmed   — the timer is armed and will run without a gesture
 *
 * `readiness === null` means "not answered yet" and is its own phase, so the
 * first paint can never claim a state it has not verified.
 */
import type { BackupReadiness } from '@data/backup/diskBackupTarget';

export interface DiskBackupViewInput {
  /** True when this platform can write to a real folder (not in-memory). */
  readonly durable: boolean;
  /** Non-prompting folder probe; `null` while the first check is in flight. */
  readonly readiness: BackupReadiness | null;
  /**
   * A manifest was read from the folder, i.e. automatic backup is set up.
   * `null` while the manifest has not been read yet (the folder is not ready).
   */
  readonly configured: boolean | null;
  /** The scheduler's timer is armed. */
  readonly schedulerArmed: boolean;
  /** The folder's real name (`DirectoryHandle.name`), never a path. */
  readonly folderLabel: string;
}

export type DiskBackupPhase = 'unsupported' | 'checking' | 'off' | 'paused' | 'on';

/**
 * What the primary button does:
 *  - 'pick_folder' — first run: open the picker and initialise the manifest.
 *  - 'reconnect'   — a folder is saved but write permission must be re-granted.
 *                    Re-requests permission on the SAVED handle: no picker.
 *  - 'resume'      — the folder is accessible and configured; only the timer is
 *                    not armed. Arms it: no picker, no write.
 */
export type DiskBackupActionKind = 'pick_folder' | 'reconnect' | 'resume';

export interface DiskBackupView {
  readonly phase: DiskBackupPhase;
  readonly description: string;
  readonly action: { readonly kind: DiskBackupActionKind; readonly label: string } | null;
  /** Manual "Back up now" is independent of the scheduler. */
  readonly canBackupNow: boolean;
  readonly showDetails: boolean;
}

export function deriveDiskBackupView(input: DiskBackupViewInput): DiskBackupView {
  const { durable, readiness, configured, schedulerArmed, folderLabel } = input;

  if (!durable) {
    return {
      phase: 'unsupported',
      description: 'Not available in this environment.',
      action: null,
      canBackupNow: false,
      showDetails: false,
    };
  }

  // Nothing is claimed until the folder has actually been probed.
  if (readiness === null) {
    return {
      phase: 'checking',
      description: 'Checking folder access…',
      action: null,
      canBackupNow: false,
      showDetails: false,
    };
  }

  // A handle exists but write permission is not granted. This is the state that
  // used to read as "Off — choose a folder", provoking the wrong action.
  if (readiness === 'permission_lost') {
    return {
      phase: 'paused',
      description: 'Paused — the backup folder needs to be reconnected before it can be saved to.',
      action: { kind: 'reconnect', label: 'Reconnect folder' },
      canBackupNow: false,
      showDetails: false,
    };
  }

  if (readiness === 'no_folder') {
    return {
      phase: 'off',
      description: 'Off — choose a folder to start saving every change automatically.',
      action: { kind: 'pick_folder', label: 'Turn on' },
      canBackupNow: false,
      showDetails: false,
    };
  }

  // readiness === 'ready': the folder is accessible right now.
  // The folder has not been READ yet, so "configured" is still unknown. Saying
  // "Off — no backup in this folder" here would be a guess, and a wrong one for
  // every device that does have automatic backup on.
  if (configured === null) {
    return {
      phase: 'checking',
      description: 'Checking the backup folder…',
      action: null,
      canBackupNow: false,
      showDetails: false,
    };
  }

  if (!configured) {
    return {
      phase: 'off',
      description:
        'Off — this folder has no DNR Vyapar backup yet. Click "Turn on" to start saving here.',
      action: { kind: 'pick_folder', label: 'Turn on' },
      canBackupNow: false,
      showDetails: false,
    };
  }

  if (!schedulerArmed) {
    return {
      phase: 'off',
      description: 'Off — automatic backup is not running. Click "Resume automatic backup".',
      action: { kind: 'resume', label: 'Resume automatic backup' },
      // The folder is reachable and holds a backup: a manual run works even
      // though the automatic scheduler is not armed.
      canBackupNow: true,
      showDetails: true,
    };
  }

  return {
    phase: 'on',
    description: `On — saving to ${folderLabel}`,
    action: null,
    canBackupNow: true,
    showDetails: true,
  };
}
